import { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Mail, Loader2, Check } from "lucide-react";

// "Never miss {country} obituaries" mid-page email opt-in strip.
// Renders inline in the /obituaries archive so casual readers can
// subscribe without leaving the page. Warm, single-field form.
//
// Props:
//   country  — active country filter (optional; drives the copy)
//   state    — active state filter (optional; refines the copy)
//   source   — logging tag (default: "celebrate")
export default function ObituarySubscribeStrip({ country, state, source = "celebrate" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const scopeLabel = state
    ? `${state} obituaries`
    : country
      ? `${country} obituaries`
      : "new obituaries";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await http.post("/obituaries/subscribe", { email: email.trim(), country: country || null, state: state || null, source });
      setDone(true);
      toast.success("You're on the list — first email lands after the next nightly sync.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="obituary-subscribe-strip"
      className="my-12 rounded-3xl overflow-hidden border p-8 md:p-10 relative"
      style={{
        borderColor: "var(--wofy-border)",
        background: "linear-gradient(135deg, #FEF3C7 0%, #FCE7F3 60%, #FFF7ED 100%)",
      }}
    >
      <div
        className="absolute -top-10 -right-10 w-56 h-56 rounded-full opacity-40 blur-3xl pointer-events-none"
        style={{ background: "#FCD34D" }}
      />
      <div className="relative grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="overline mb-2" style={{ color: "#B45309" }}>
            NEVER MISS AN OBITUARY
          </div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight">
            Get an email when a new{" "}
            <span className="whitespace-nowrap" style={{ color: "#B45309" }}>{scopeLabel}</span>{" "}
            is posted.
          </h3>
          <p className="mt-2 text-sm max-w-md" style={{ color: "var(--wofy-text-soft)" }}>
            One thoughtful email per week, only when we import fresh photo-bearing
            obituaries. Unsubscribe in a single click.
          </p>
        </div>

        {done ? (
          <div className="flex items-center gap-3 justify-start md:justify-end" data-testid="obit-subscribe-done">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#DCFCE7" }}>
              <Check className="w-5 h-5" style={{ color: "#166534" }} />
            </div>
            <div>
              <div className="font-display text-lg">You're subscribed.</div>
              <div className="text-xs opacity-70">First email arrives after the next nightly sync.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="obit-subscribe-form">
            <div className="relative flex-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourfamily.com"
                data-testid="obit-subscribe-email"
                className="w-full rounded-full border pl-10 pr-4 py-3 text-sm bg-white/80"
                style={{ borderColor: "var(--wofy-border)" }}
              />
            </div>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              data-testid="obit-subscribe-submit"
              className="wofy-btn-primary inline-flex items-center gap-2 justify-center whitespace-nowrap"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Subscribe
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
